import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fetchClients, createDossier } from "@/lib/dossiers";
import ClientSection from "@/components/dossiers/ClientSection";
import EntrepriseSection from "@/components/dossiers/EntrepriseSection";
import MissionSection, { rangeYears } from "@/components/dossiers/MissionSection";
import { requiredTypes } from "@/lib/checklist";

const emptyClient = { id: "", email: "", prenom: "", nom: "", type_professionnel: "", nom_cabinet: "" };
const emptyEntreprise = { siren: "", nom_entreprise: "", forme_juridique: "", nom_gerant: "", adresse: "" };

export default function NouveauDossier() {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [mode, setMode] = useState("existing");
    const [client, setClient] = useState(emptyClient);
    const [entreprise, setEntreprise] = useState(emptyEntreprise);
    const [notes, setNotes] = useState("");
    const [nomMission, setNomMission] = useState("");
    const [missionTouched, setMissionTouched] = useState(false);
    const [autoSearch, setAutoSearch] = useState(0);

    function pickClient(next) {
        setClient(next);
        const c = clients.find((x) => x.id === next.id);
        const ent = c?.entreprises?.[0];
        if (ent?.siren) {
            setEntreprise((s) => ({ ...s, siren: ent.siren, nom_entreprise: ent.nom_entreprise || s.nom_entreprise }));
            setAutoSearch((n) => n + 1);
        }
    }

    useEffect(() => {
        if (missionTouched) return;
        const nom = entreprise.nom_entreprise.trim();
        setNomMission(nom ? `${nom} - Mission ${new Date().toLocaleDateString("fr-FR")}` : "");
    }, [entreprise.nom_entreprise, missionTouched]);
    const [exercice, setExercice] = useState({ debut: null, fin: null });
    const [contexte, setContexte] = useState("");
    const [contexteAutre, setContexteAutre] = useState("");
    const [documentsRequis, setDocumentsRequis] = useState([]);
    const [requisTouched, setRequisTouched] = useState(false);
    const [enImmat, setEnImmat] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    // Phase B — tant que l'experte n'a pas personnalisé la liste, elle suit la matrice par défaut du contexte.
    useEffect(() => {
        if (!requisTouched) setDocumentsRequis(contexte ? requiredTypes(contexte, "general") : []);
    }, [contexte, requisTouched]);

    useEffect(() => {
        fetchClients()
            .then((list) => {
                setClients(list);
                if (list.length === 0) setMode("new");
            })
            .catch((e) => setError(e.message));
    }, []);

    function validate() {
        if (!nomMission.trim()) return "Le nom de la mission est obligatoire (ex. Audit Restructuration 2026).";
        if (mode === "existing" && !client.id) return "Sélectionnez un client.";
        if (mode === "new" && (!client.prenom.trim() || !client.nom.trim() || !client.email.trim())) return "Prénom, nom et e-mail du client sont requis.";
        if (!enImmat && !/^\d{9}$/.test(entreprise.siren)) return "Le SIREN doit contenir exactement 9 chiffres (ou cochez « Entreprise en cours d'immatriculation »).";
        if (!entreprise.nom_entreprise.trim()) return "Le nom de l'entreprise est requis.";
        if (!contexte) return "Le contexte de la mission est obligatoire.";
        if (contexte === "autre" && !contexteAutre.trim()) return "Précisez le contexte de la mission.";
        if ((exercice.debut && !exercice.fin) || (!exercice.debut && exercice.fin)) return "Indiquez le premier et le dernier exercice.";
        return "";
    }

    async function submit(e) {
        e.preventDefault();
        const v = validate();
        if (v) return setError(v);
        setError("");
        setBusy(true);
        try {
            const created = await createDossier({
                nom_mission: nomMission.trim(),
                client: mode === "existing" ? { id: client.id } : { ...client, id: null, prenom_nom: `${client.prenom.trim()} ${client.nom.trim()}` },
                entreprise: { ...entreprise, en_immatriculation: enImmat },
                type_dossier: "general",
                notes,
                exercices_fiscaux: rangeYears(exercice.debut, exercice.fin),
                contexte_audit: contexte,
                contexte_autre: contexte === "autre" ? contexteAutre.trim() : null,
                documents_requis: documentsRequis,
            });
            toast.success("Dossier créé", { description: created.entreprise_reutilisee ? `${nomMission.trim()} — rattaché à l'entreprise existante ${entreprise.nom_entreprise}.` : `${nomMission.trim()} — ${entreprise.nom_entreprise}.` });
            navigate(`/dossiers/${created.id}`);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div data-testid="nouveau-dossier-page" className="space-y-10">
            <header className="border-b border-border pb-8">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Créer une mission</p>
                <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
                    Nouveau <span className="italic text-muted-foreground">dossier</span>
                </h1>
                <p className="mt-4 max-w-xl text-sm text-muted-foreground">
                    Renseignez le client et l&apos;entreprise auditée : une même entreprise peut faire l&apos;objet de plusieurs missions, distinguées par leur nom. Le type de rapport (audit, restructuration…) se choisit au moment de générer la synthèse IA.
                </p>
            </header>

            <form onSubmit={submit} data-testid="nouveau-dossier-form" className="max-w-3xl space-y-12">
                <div className="flex justify-end -mb-6">
                    <Button type="submit" data-testid="submit-dossier-button-top" disabled={busy} className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground">{busy ? "Création…" : "Créer le dossier"}</Button>
                </div>
                <ClientSection clients={clients} mode={mode} setMode={setMode} client={client} setClient={(v) => (typeof v === "function" ? pickClient(v(client)) : pickClient(v))} />
                <EntrepriseSection entreprise={entreprise} setEntreprise={setEntreprise} autoSearch={autoSearch} enImmat={enImmat} setEnImmat={setEnImmat} />
                <MissionSection nomMission={nomMission} setNomMission={(v) => { setMissionTouched(true); setNomMission(v); }} notes={notes} setNotes={setNotes} exercice={exercice} setExercice={setExercice} contexte={contexte} setContexte={setContexte} contexteAutre={contexteAutre} setContexteAutre={setContexteAutre} documentsRequis={documentsRequis} setDocumentsRequis={(v) => { setRequisTouched(true); setDocumentsRequis(v); }} />

                {error && (
                    <p data-testid="form-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">
                        {error}
                    </p>
                )}

                <div className="flex items-center gap-4 pt-2">
                    <Button
                        type="submit"
                        data-testid="submit-dossier-button"
                        disabled={busy}
                        className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        {busy ? "Création…" : "Créer le dossier"}
                    </Button>
                    <Button asChild variant="ghost" data-testid="btn-back-dashboard" className="rounded-full h-11 px-5 text-muted-foreground">
                        <Link to="/dashboard">
                            <ArrowLeft className="h-4 w-4" strokeWidth={2} />
                            Annuler
                        </Link>
                    </Button>
                </div>
            </form>
        </div>
    );
}
